import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { FileCheck, CheckCircle, ArrowRight } from "lucide-react";

const NISTAssessmentsService = () => {
  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="pt-24 pb-16 bg-secondary/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <FileCheck className="w-12 h-12 text-accent" />
              <h1 className="text-4xl md:text-5xl font-bold">NIST Assessments</h1>
            </div>
            
            <p className="text-xl text-muted-foreground mb-8">
              Comprehensive assessments against NIST Cyber Security Framework to strengthen your security posture.
            </p>

            <div className="prose prose-lg max-w-none mb-12">
              <h2 className="text-2xl font-bold mb-4">NIST Cyber Security Framework</h2>
              <p className="mb-6">
                The NIST Cyber Security Framework provides a policy framework of computer security guidance for how organisations can assess and improve their ability to prevent, detect, and respond to cyber attacks. Covering 106 subcategories across five core functions, NIST CSF offers a comprehensive yet flexible approach to cyber security that scales with your organisation.
              </p>

              <h2 className="text-2xl font-bold mb-4">Assessment Process</h2>
              <div className="bg-secondary/20 rounded-lg p-6 mb-6">
                <p className="mb-3"><strong>3-Week Structured Engagement:</strong></p>
                <ul className="space-y-2">
                  <li><strong>Week 1:</strong> Documentation review and gap analysis across 106 subcategories</li>
                  <li><strong>Week 2:</strong> Stakeholder interviews (10-15 people across IT, security, and business)</li>
                  <li><strong>Week 3:</strong> Technical assessments, scoring, and roadmap development</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold mb-4">Maturity Scoring</h2>
              <p className="mb-4">We evaluate your organisation across five maturity tiers:</p>
              <div className="space-y-3 mb-6">
                <div className="border-l-4 border-accent pl-4">
                  <p className="font-semibold">Tier 1 - Partial:</p>
                  <p className="text-muted-foreground text-sm">Ad hoc, reactive approach with limited awareness</p>
                </div>
                <div className="border-l-4 border-accent pl-4">
                  <p className="font-semibold">Tier 2 - Risk Informed:</p>
                  <p className="text-muted-foreground text-sm">Risk management practices approved but not policy-based</p>
                </div>
                <div className="border-l-4 border-accent pl-4">
                  <p className="font-semibold">Tier 3 - Repeatable:</p>
                  <p className="text-muted-foreground text-sm">Formal policies with consistent implementation</p>
                </div>
                <div className="border-l-4 border-accent pl-4">
                  <p className="font-semibold">Tier 4 - Adaptive:</p>
                  <p className="text-muted-foreground text-sm">Adaptive approach based on lessons learned and predictive indicators</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold mb-4">What You'll Receive</h2>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Current state profile:</strong> Detailed scoring across all five functions (Identify, Protect, Detect, Respond, Recover)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Target profile:</strong> Recommended maturity levels based on your risk tolerance and industry</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Gap analysis report:</strong> Prioritized list of gaps with risk and effort assessments</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>12-24 month roadmap:</strong> Phased implementation plan with prioritized initiatives</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Executive summary:</strong> Board-ready presentation of findings and recommendations</span>
                </li>
              </ul>

              <h2 className="text-2xl font-bold mb-4">Typical Findings</h2>
              <div className="bg-accent/10 rounded-lg p-6 mb-6">
                <p className="mb-3">Most organisations we assess show:</p>
                <ul className="space-y-2">
                  <li>• <strong>Average Tier 2 maturity</strong> at initial assessment</li>
                  <li>• <strong>Strong Identify function</strong> (asset management, risk assessment)</li>
                  <li>• <strong>Weak Detect and Respond functions</strong> (monitoring, incident response)</li>
                  <li>• <strong>Tier 3 achievable</strong> within 12-18 months with focused effort</li>
                  <li>• <strong>Quick wins</strong> available in access management and awareness training</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold mb-4">Framework Benefits</h2>
              <ul className="space-y-2">
                <li>✓ <strong>Common language</strong> for discussing cyber risk with technical and non-technical stakeholders</li>
                <li>✓ <strong>Risk-based prioritization</strong> ensuring resources focus on highest-impact areas</li>
                <li>✓ <strong>Compliance mapping</strong> to ISO 27001, SOC 2, and other frameworks</li>
                <li>✓ <strong>Continuous improvement</strong> model supporting ongoing security enhancement</li>
                <li>✓ <strong>Industry recognition</strong> as a globally respected framework</li>
              </ul>
            </div>

            <div className="bg-secondary/30 rounded-lg p-8">
              <h3 className="text-2xl font-bold mb-4">Ready to assess your security against NIST CSF?</h3>
              <p className="text-muted-foreground mb-6">
                Let's evaluate your current security posture and develop a roadmap for continuous improvement.
              </p>
              <a 
                href="https://outlook.office365.com/owa/calendar/HazelMcPherson4FOXSecurity@4fox.uk/bookings/" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button size="lg" className="bg-accent hover:bg-accent/90">
                  Book a Consultation
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default NISTAssessmentsService;
